import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, linkedSignal, output, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, debounceTime, distinctUntilChanged, map, of, startWith, switchMap } from 'rxjs';
import {
  type AddPagesItem,
  type FeedbackRound,
  type FeedbackRoundPage,
  FeedbackRoundService,
  PageStatus,
  type RoundPages,
} from '@brafton/feedback-api';
import { Pagination, StatusChip } from '@brafton/feedback-components';
import { pageReviewLink } from '../../core/page-review-link';

/** Pages shown per page of the list. */
const PAGES_PER_PAGE = 50;

/** How long the URL search waits after the last keystroke before it queries. */
const SEARCH_DEBOUNCE_MS = 300;

/** The list's query: which page of which round, filtered how. */
interface PagesQuery {
  roundId: string;
  page: number;
  search: string;
  status: PageStatus | '';
  reloadCount: number;
}

/** The list request's state: in flight, or what came back (null on failure). */
interface PagesState {
  loading: boolean;
  pagesPage: RoundPages | null;
}

/** A mockup resolved for the picker: full URL + filename label + thumbnail. */
interface MockupOption {
  value: string;
  label: string;
  thumbnail: string;
}

/** Key used for the add-page row's mockup dropdown (vs. a real page's URL). */
const ADD_ROW_KEY = '__add__';

/**
 * Collapsible "Pages" panel: lists the round's tracked pages (URL, assigned mockup,
 * status), 50 at a time from the API, searchable by URL and filterable by status —
 * rounds can have tens of thousands of pages, so they're never loaded all at once.
 *
 * The mockup is a thumbnail dropdown that reassigns from the round's uploaded
 * mockups, and "Add Page" reveals an inline row to add a page URL with a chosen
 * mockup. Both go through `assignMockupPages`; a row's status chip saves through
 * `updatePageStatus`. After any change the list reloads and `pagesChanged` fires.
 */
@Component({
  selector: 'app-pages-panel',
  imports: [Pagination, StatusChip],
  templateUrl: './pages-panel.html',
  styleUrl: './pages-panel.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PagesPanel {
  /** The feedback round these pages belong to. */
  readonly feedbackRoundId = input<string>('');

  /** The round's uploaded mockup URLs, offered in the mockup dropdowns. */
  readonly mockups = input<string[]>([]);

  /** Available page-status values for each row's status chip dropdown. */
  readonly statuses = input<string[]>([]);

  /** Emitted with the updated round (no pages) after a mockup is (re)assigned or pages added. */
  readonly roundUpdated = output<FeedbackRound>();

  /** Emitted after the round's pages change (added, mockup reassigned, status changed). */
  readonly pagesChanged = output<void>();

  private readonly rounds = inject(FeedbackRoundService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly addRowKey = ADD_ROW_KEY;
  protected readonly perPage = PAGES_PER_PAGE;
  /** Status filter choices, labeled like the status chips (`internal_review` → `Internal Review`). */
  protected readonly statusFilterOptions = Object.values(PageStatus).map((status) => ({
    value: status,
    label: status.replace(/_/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase()),
  }));

  /** The URL search as typed; the query uses a debounced copy. */
  protected readonly searchText = signal('');
  private readonly debouncedSearch = toSignal(
    toObservable(this.searchText).pipe(
      debounceTime(SEARCH_DEBOUNCE_MS),
      map((text) => text.trim()),
      distinctUntilChanged(),
    ),
    { initialValue: '' },
  );
  protected readonly statusFilter = signal<PageStatus | ''>('');
  /** Bumped to refetch the current page after a change. */
  private readonly reloadCount = signal(0);

  /** Current list page; back to 1 whenever the round, search or status filter changes. */
  protected readonly currentPage = linkedSignal(() => {
    this.feedbackRoundId();
    this.debouncedSearch();
    this.statusFilter();
    return 1;
  });

  private readonly query = computed<PagesQuery>(() => ({
    roundId: this.feedbackRoundId(),
    page: this.currentPage(),
    search: this.debouncedSearch(),
    status: this.statusFilter(),
    reloadCount: this.reloadCount(),
  }));

  private readonly pagesState = toSignal(
    toObservable(this.query).pipe(
      switchMap((pagesQuery) =>
        pagesQuery.roundId
          ? this.rounds
              .listPages(pagesQuery.roundId, {
                page: pagesQuery.page,
                perPage: PAGES_PER_PAGE,
                search: pagesQuery.search || undefined,
                status: pagesQuery.status || undefined,
              })
              .pipe(
                map((pagesPage): PagesState => ({ loading: false, pagesPage })),
                catchError(() => of<PagesState>({ loading: false, pagesPage: null })),
                startWith<PagesState>({ loading: true, pagesPage: null }),
              )
          : of<PagesState>({ loading: false, pagesPage: null }),
      ),
    ),
    { initialValue: { loading: true, pagesPage: null } as PagesState },
  );

  /** The latest results, kept on screen while the next request is in flight. */
  private readonly loadedPages = linkedSignal<PagesState, RoundPages | null>({
    source: this.pagesState,
    computation: (state, previous) =>
      state.pagesPage ?? (state.loading ? (previous?.value ?? null) : null),
  });

  protected readonly loading = computed(() => this.pagesState().loading);

  /** Status edits made on screen, shown until the reload after them lands. */
  private readonly statusOverrides = linkedSignal<RoundPages | null, ReadonlyMap<string, PageStatus>>({
    source: this.loadedPages,
    computation: () => new Map(),
  });

  protected readonly pages = computed<FeedbackRoundPage[]>(() => {
    const statusOverridesByPageUrl = this.statusOverrides();
    return (this.loadedPages()?.results ?? []).map((roundPage) => {
      const overriddenStatus = statusOverridesByPageUrl.get(roundPage.pageUrl);
      return overriddenStatus ? { ...roundPage, status: overriddenStatus } : roundPage;
    });
  });

  /** Pages matching the search/status filter. */
  protected readonly filteredTotal = computed(() => this.loadedPages()?.total ?? 0);
  protected readonly isFiltered = computed(() => !!this.debouncedSearch() || !!this.statusFilter());

  /** Whether the accordion is expanded. */
  protected readonly open = signal(false);
  /** Whether the inline add-page row is shown. */
  protected readonly adding = signal(false);
  /** Key of the row whose mockup dropdown is open (page URL or the add-row key). */
  protected readonly openMockupFor = signal<string | null>(null);
  /** True while an assign/add request is in flight. */
  protected readonly busy = signal(false);

  // Add-page draft.
  protected readonly draftPageUrl = signal('');
  protected readonly draftMockup = signal('');

  protected readonly mockupOptions = computed<MockupOption[]>(() =>
    this.mockups().map((url) => ({ value: url, label: this.fileName(url), thumbnail: url })),
  );

  protected readonly canAddPage = computed(
    () => !!this.draftPageUrl().trim() && !!this.draftMockup() && !this.busy(),
  );

  // Whole-round stats (Review groups internal + client review), unaffected by the filters.
  protected readonly totalCount = computed(() => this.countOf(...Object.values(PageStatus)));
  protected readonly reviewCount = computed(() =>
    this.countOf(PageStatus.InternalReview, PageStatus.ClientReview),
  );
  protected readonly reviseCount = computed(() => this.countOf(PageStatus.Revise));
  protected readonly approvedCount = computed(() => this.countOf(PageStatus.Approved));

  private countOf(...statuses: PageStatus[]): number {
    const statusCounts = this.loadedPages()?.statusCounts;
    return statuses.reduce((sum, status) => sum + (statusCounts?.[status] ?? 0), 0);
  }

  protected onStatusFilterChange(status: string): void {
    this.statusFilter.set(status as PageStatus | '');
  }

  protected onPageChange(page: number): void {
    this.openMockupFor.set(null);
    this.currentPage.set(page);
  }

  /** Refetches the current page (and the counts) and tells the host. */
  private reload(): void {
    this.reloadCount.update((reloadTotal) => reloadTotal + 1);
    this.pagesChanged.emit();
  }

  protected toggle(): void {
    this.open.update((isOpen) => !isOpen);
  }

  protected startAdding(event: Event): void {
    event.stopPropagation();
    this.open.set(true);
    this.adding.set(true);
  }

  protected cancelAdding(): void {
    this.adding.set(false);
    this.draftPageUrl.set('');
    this.draftMockup.set('');
    this.openMockupFor.set(null);
  }

  protected toggleMockupMenu(key: string): void {
    this.openMockupFor.update((current) => (current === key ? null : key));
  }

  /** The page's full URL, used as the assign target (backend derives the base). */
  protected pageTarget(page: FeedbackRoundPage): string {
    return page.fullUrl ?? page.pageUrl;
  }

  /** The page's link carries the widget parameter on client sites. */
  protected readonly pageReviewLink = pageReviewLink;

  /** Only http(s) URLs link out to the page, never a `javascript:` or relative one. */
  protected isWebUrl(url: string): boolean {
    return /^https?:\/\//i.test(url ?? '');
  }

  /** Filename label for a mockup URL, or null when none is assigned. */
  protected mockupLabel(url: string | null | undefined): string | null {
    return url ? this.fileName(url) : null;
  }

  /** Reassigns a mockup to an existing page. */
  protected selectRowMockup(page: FeedbackRoundPage, mockupUrl: string): void {
    this.openMockupFor.set(null);
    this.assign(this.pageTarget(page), mockupUrl);
  }

  /** Picks a mockup for the add-page draft (no request yet). */
  protected selectDraftMockup(mockupUrl: string): void {
    this.draftMockup.set(mockupUrl);
    this.openMockupFor.set(null);
  }

  /** Adds a new page with the chosen mockup. */
  protected addPage(): void {
    if (!this.canAddPage()) {
      return;
    }
    this.assign(this.draftPageUrl().trim(), this.draftMockup(), () => this.cancelAdding());
  }

  /** Reads the chosen CSV, parses valid page/mockup pairs, and imports them. */
  protected onCsvSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    input.value = '';
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const items = this.parseCsvPages(String(reader.result ?? ''));
      if (!items.length) {
        this.snackBar.open('No valid page/mockup rows found in the CSV.', 'Dismiss', {
          duration: 5000,
        });
        return;
      }
      this.importPages(items);
    };
    reader.readAsText(file);
  }

  /**
   * Parses CSV text into page/mockup pairs, keeping ONLY valid rows: the page
   * must be a URL, and the mockup must be either empty or a URL. This drops
   * header rows and any extra/garbage columns (only the first two are read).
   */
  private parseCsvPages(text: string): AddPagesItem[] {
    const isUrl = (value: string): boolean => /^https?:\/\//i.test(value);
    const clean = (value: string): string => value.trim().replace(/^"(.*)"$/, '$1').trim();

    const items: AddPagesItem[] = [];
    const seen = new Set<string>();
    for (const rawLine of text.split(/\r?\n/)) {
      if (!rawLine.trim()) {
        continue;
      }
      const cells = rawLine.split(',').map(clean);
      const pageUrl = cells[0] ?? '';
      const mockupUrl = cells[1] ?? '';
      if (!isUrl(pageUrl)) {
        continue;
      }
      if (mockupUrl !== '' && !isUrl(mockupUrl)) {
        continue;
      }
      if (seen.has(pageUrl)) {
        continue;
      }
      seen.add(pageUrl);
      items.push({ pageUrl, mockupUrl });
    }
    return items;
  }

  /** Sends the parsed pairs to the bulk add-pages endpoint. */
  private importPages(items: AddPagesItem[]): void {
    const roundId = this.feedbackRoundId();
    if (!roundId) {
      return;
    }
    this.busy.set(true);
    this.rounds
      .addPages(roundId, { pages: items })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (round) => {
          this.busy.set(false);
          this.open.set(true);
          this.roundUpdated.emit(round);
          this.reload();
          this.snackBar.open(
            `Imported ${items.length} page${items.length === 1 ? '' : 's'}.`,
            'Dismiss',
            { duration: 4000 },
          );
        },
        error: () => {
          this.busy.set(false);
          this.snackBar.open('Could not import pages. Please try again.', 'Dismiss', {
            duration: 5000,
          });
        },
      });
  }

  /** Assigns a mockup to a page URL (creates the page if it's new) and emits the result. */
  private assign(pageUrl: string, mockupUrl: string, onSuccess?: () => void): void {
    const roundId = this.feedbackRoundId();
    if (!roundId) {
      return;
    }
    this.busy.set(true);
    this.rounds
      .assignMockupPages(roundId, { pageUrl, mockupUrl })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (round) => {
          this.busy.set(false);
          this.roundUpdated.emit(round);
          this.reload();
          onSuccess?.();
        },
        error: () => {
          this.busy.set(false);
          this.snackBar.open('Could not update the page. Please try again.', 'Dismiss', {
            duration: 5000,
          });
        },
      });
  }

  /** Saves a page's new status, showing it straight away and reverting on failure. */
  protected onStatusChange(page: FeedbackRoundPage, status: string): void {
    const roundId = this.feedbackRoundId();
    const nextStatus = status as PageStatus;
    if (!roundId || page.status === nextStatus) {
      return;
    }
    this.setStatusOverride(page.pageUrl, nextStatus);
    this.rounds
      .updatePageStatus(roundId, nextStatus, page.pageUrl)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.reload(),
        error: () => {
          this.setStatusOverride(page.pageUrl, null);
          this.snackBar.open('Could not update the page status. Reverted.', 'Dismiss', {
            duration: 5000,
          });
        },
      });
  }

  private setStatusOverride(pageUrl: string, status: PageStatus | null): void {
    this.statusOverrides.update((overrides) => {
      const nextOverrides = new Map(overrides);
      if (status === null) {
        nextOverrides.delete(pageUrl);
      } else {
        nextOverrides.set(pageUrl, status);
      }
      return nextOverrides;
    });
  }

  /** The trailing file name of a URL, for the mockup label. */
  private fileName(url: string): string {
    try {
      const path = new URL(url).pathname;
      return decodeURIComponent(path.split('/').filter(Boolean).pop() ?? url);
    } catch {
      return url.split('/').filter(Boolean).pop() ?? url;
    }
  }
}
